import { makeAutoObservable, runInAction } from 'mobx'
import { useEffect } from 'react'
import type { APIOrganizationMemberRole, APIUser } from '@visorpro/client'

import { visorPRORestClient } from '../util/api'
import { observed } from '../util/observed-decorator'

export class OrganizationMembersStore {
    public organizationId: string | null = null
    public membersById: Record<string, APIUser> = {}
    public isFetching = false
    public isUpdating = false

    constructor() {
        makeAutoObservable(this)
    }

    @observed('isFetching')
    public async fetchOrganizationMembers(organizationId: string) {
        runInAction(() => {
            this.organizationId = organizationId
        })

        const response =
            await visorPRORestClient.organization.getMembers(organizationId)

        runInAction(() => {
            this.membersById = {}
            this.organizationId = organizationId
            response.items.forEach((user: APIUser) => {
                this.membersById[user.id] = user
            })
        })
    }

    public async addOrganizationMembership(
        userId: string,
        organizationId: string,
    ) {
        const membership =
            await visorPRORestClient.organization.createMembership(
                organizationId,
                userId,
            )

        runInAction(() => {
            // The create response carries the new membership (with its role) but
            // returns the user without memberships populated; attach it so the
            // Role column renders immediately instead of blank until refetch.
            this.membersById[userId] = {
                ...membership.user!,
                memberships: [membership],
            }
        })
    }

    @observed('isUpdating')
    public async updateOrganizationMembershipRole(
        userId: string,
        organizationId: string,
        role: APIOrganizationMemberRole,
    ) {
        await visorPRORestClient.organization.updateMembership(
            organizationId,
            userId,
            role,
        )

        runInAction(() => {
            const user = this.membersById[userId]
            if (!user) return
            this.membersById[userId] = {
                ...user,
                memberships: user.memberships?.map((membership) =>
                    membership.organization_id === organizationId
                        ? { ...membership, role }
                        : membership,
                ),
            }
        })
    }

    public async removeOrganizationMembership(
        userId: string,
        organizationId: string,
    ) {
        await this.removeOrganizationMemberships([userId], organizationId)
    }

    public async removeOrganizationMemberships(
        userIds: string[],
        organizationId: string,
    ) {
        await Promise.all(
            userIds.map((userId) =>
                visorPRORestClient.organization.deleteMembership(
                    organizationId,
                    userId,
                ),
            ),
        )

        runInAction(() => {
            userIds.forEach((userId) => {
                delete this.membersById[userId]
            })
        })
    }
}

export const useOrganizationMembers = (
    store: OrganizationMembersStore,
    organizationId: string,
) => {
    useEffect(() => {
        if (organizationId !== store.organizationId) {
            void store.fetchOrganizationMembers(organizationId)
        }
    }, [organizationId, store, store.organizationId])

    return Object.values(store.membersById)
}
